import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import dayjs from '~/timeInit';

import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import PageHeader from '~/components/Common/Elements/PageHeader';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import Spinner from '~/components/Common/Spinner';

import { useGetAtolCertificateById } from '~/hooks/apis/travel-compliance/useGetAtolCertificateById';
import { usePatchAtolCertificateItems } from '~/hooks/apis/travel-compliance/usePatchAtolCertificateItems';

import AtolCertificateHtmlViewer from './AtolCertificateHtmlViewer';
import AtolItemContentEditor from './AtolItemContentEditor';
import { CERTIFICATE_STATUS } from './atolConstants';
import {
  type AnyItem,
  type DateValidationError,
  type EditableItem,
  ITEM_GROUP_KEYS,
  ITEM_GROUP_LABELS,
  type TravellerEditState,
  buildEditableItems,
  buildPatchPayloadWithTravellers,
  buildTravellerEditState,
  countChanges,
  getReferenceLabel,
  getReferenceValue,
  validateDates,
} from './atolEditHelpers';

function decodeHtmlEntities(html: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
}

export default function AtolCertificateEditPage() {
  const { id_atol_certificate: certificateId } = useParams<{ id_atol_certificate: string }>();
  const { data: certificate, isLoading, error } = useGetAtolCertificateById(certificateId);
  const patchMutation = usePatchAtolCertificateItems(certificateId);
  const { enqueueSnackbar } = useSnackbar();
  const showConfirmDialog = useConfirmDialog();

  const [editableItems, setEditableItems] = useState<Array<EditableItem>>([]);
  const [travellerState, setTravellerState] = useState<TravellerEditState | null>(null);
  const [initialised, setInitialised] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewMessage, setPreviewMessage] = useState<string | null>(null);
  const [previewedAt, setPreviewedAt] = useState<Date | null>(null);
  const [sendStatus, setSendStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [submittedSuccessfully, setSubmittedSuccessfully] = useState(false);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (certificate && !initialised) {
      if (certificate.items) {
        setEditableItems(buildEditableItems(certificate.items));
      }
      if (certificate.travellers) {
        setTravellerState(buildTravellerEditState(certificate.travellers));
      }
      setInitialised(true);
    }
  }, [certificate, initialised]);

  const isAnyLoading = patchMutation.isPending;
  const changeCount = useMemo(
    () => countChanges(editableItems, travellerState ?? undefined),
    [editableItems, travellerState],
  );
  const hasAnyChanges = changeCount > 0;
  const dateErrors: Array<DateValidationError> = useMemo(() => validateDates(editableItems), [editableItems]);
  const hasDateErrors = dateErrors.length > 0;

  const isEditable = certificate?.certificateStatus === CERTIFICATE_STATUS.ACTIVE;

  // -- Item update handlers -------------------------------------------------

  const toggleDeleteItem = useCallback(
    async (itemId: string) => {
      const matchedItem = editableItems.find((editItem) => editItem.current.id === itemId);
      if (!matchedItem) {
        return;
      }

      const isCurrentlyDeleted = matchedItem.deleted;
      if (!isCurrentlyDeleted) {
        const isFlightItem = matchedItem.groupKey === 'flights' || matchedItem.groupKey === 'offlineFlights';
        if (isFlightItem) {
          const confirmed = await showConfirmDialog({
            title: 'Warning: Removing Flight',
            description:
              'Removing a flight may result in the certificate being cancelled. Are you sure you want to proceed?',
          });
          if (!confirmed) {
            return;
          }
        }
      }

      setEditableItems((prev) =>
        prev.map((editItem) =>
          editItem.current.id === itemId ? { ...editItem, deleted: !editItem.deleted } : editItem,
        ),
      );
    },
    [editableItems, showConfirmDialog],
  );

  const updateItemField = useCallback((itemId: string, updater: (current: AnyItem) => AnyItem) => {
    setEditableItems((prev) =>
      prev.map((editItem) =>
        editItem.current.id === itemId ? { ...editItem, current: updater(editItem.current) } : editItem,
      ),
    );
  }, []);

  const updateTravellerField = useCallback((index: number, field: 'givenName' | 'surname', value: string) => {
    setTravellerState((prev) => {
      if (!prev) {
        return prev;
      }
      const updated = [...prev.current];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, current: updated };
    });
  }, []);

  // -- Preview / Send -------------------------------------------------------

  const handlePreview = useCallback(() => {
    if (!hasAnyChanges) {
      enqueueSnackbar('No changes to preview', { variant: 'info' });
      return;
    }

    const payload = buildPatchPayloadWithTravellers(certificateId, editableItems, travellerState ?? undefined);
    if (!payload) {
      return;
    }

    setSendStatus(null);
    patchMutation.mutate(
      { payload, dryRun: true },
      {
        onSuccess: (response) => {
          if (!isMountedRef.current) {
            return;
          }
          const result = response.result;
          if (result.certificateHtml) {
            setPreviewHtml(decodeHtmlEntities(result.certificateHtml));
          }
          setPreviewMessage(result.message);
          setPreviewedAt(new Date());
          enqueueSnackbar('Preview generated', { variant: 'success' });
        },
      },
    );
  }, [certificateId, editableItems, travellerState, hasAnyChanges, enqueueSnackbar, patchMutation]);

  const handleSend = useCallback(async () => {
    if (!hasAnyChanges) {
      enqueueSnackbar('No changes to submit', { variant: 'info' });
      return;
    }

    const confirmDescription = previewHtml
      ? `You have previewed the updated certificate. Are you sure all information is correct and you want to send the updated ATOL certificate to ${
          certificate?.customerEmail ?? 'the customer'
        }?`
      : `Warning: You have not previewed the certificate after your changes. Are you sure all information is correct and you want to send the updated ATOL certificate to ${
          certificate?.customerEmail ?? 'the customer'
        }?`;

    const confirmed = await showConfirmDialog({
      title: 'Confirm Update ATOL Certificate',
      description: confirmDescription,
    });
    if (!confirmed) {
      return;
    }

    const payload = buildPatchPayloadWithTravellers(certificateId, editableItems, travellerState ?? undefined);
    if (!payload) {
      return;
    }

    setSendStatus(null);
    patchMutation.mutate(
      { payload, dryRun: false },
      {
        onSuccess: (response) => {
          if (!isMountedRef.current) {
            return;
          }
          const result = response.result;
          setSendStatus({ success: true, message: result.message });
          enqueueSnackbar(result.message, { variant: 'success' });
          setPreviewHtml(null);
          setPreviewMessage(null);
          setPreviewedAt(null);
          setSubmittedSuccessfully(true);
        },
      },
    );
  }, [
    certificate?.customerEmail,
    certificateId,
    editableItems,
    travellerState,
    enqueueSnackbar,
    hasAnyChanges,
    patchMutation,
    previewHtml,
    showConfirmDialog,
  ]);

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------

  const showEditableContent = isEditable || submittedSuccessfully;

  return (
    <Container>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={48} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error.message || 'Failed to load certificate details'}
        </Alert>
      ) : !certificate ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Certificate not found
        </Alert>
      ) : (
        <>
          <PageHeader
            title="Edit ATOL Certificate"
            subtitle={
              <Typography variant="h4">
                {certificate.uniqueReferenceNumber} - Version {certificate.version}
              </Typography>
            }
            backButton
          />

          {!isEditable && !submittedSuccessfully && (
            <Alert severity="error" sx={{ mt: 2 }}>
              This certificate is <strong>{certificate.certificateStatus}</strong> and cannot be edited. Only
              certificates with ACTIVE status can be modified.
            </Alert>
          )}

          {showEditableContent && (
            <>
              {/* Travellers — editable names */}
              <Card sx={{ mt: 2 }}>
                <CardHeader
                  title={<Typography variant="h2">Travellers ({travellerState?.current.length ?? 0})</Typography>}
                />
                <CardContent>
                  <Stack spacing={2} divider={<Divider />}>
                    {travellerState?.current.map((traveller, index) => (
                      <Grid container spacing={2} key={index} alignItems="center">
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Traveller {index + 1}
                            {traveller.type && (
                              <Chip label={traveller.type} size="small" sx={{ ml: 1 }} variant="outlined" />
                            )}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <TextField
                            label="Given Name"
                            value={traveller.givenName}
                            onChange={(e) => updateTravellerField(index, 'givenName', e.target.value)}
                            fullWidth
                            disabled={isAnyLoading || !isEditable}
                            required
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <TextField
                            label="Surname"
                            value={traveller.surname}
                            onChange={(e) => updateTravellerField(index, 'surname', e.target.value)}
                            fullWidth
                            disabled={isAnyLoading || !isEditable}
                            required
                          />
                        </Grid>
                      </Grid>
                    ))}
                  </Stack>
                </CardContent>
              </Card>

              {/* Items — grouped by type */}
              {ITEM_GROUP_KEYS.map((groupKey) => {
                const groupItems = editableItems.filter((editItem) => editItem.groupKey === groupKey);
                if (groupItems.length === 0) {
                  return null;
                }

                const activeCount = groupItems.filter((editItem) => !editItem.deleted).length;
                const refLabel = getReferenceLabel(groupKey);

                return (
                  <Card sx={{ mt: 2 }} key={groupKey}>
                    <CardHeader
                      title={
                        <Typography variant="h2">
                          {ITEM_GROUP_LABELS[groupKey]} ({activeCount}/{groupItems.length})
                        </Typography>
                      }
                    />
                    <CardContent>
                      <Stack spacing={3} divider={<Divider />}>
                        {groupItems.map((editItem) => {
                          const isRemoved = editItem.deleted;
                          const refValue = getReferenceValue(groupKey, editItem.current);

                          return (
                            <Box key={editItem.current.id} sx={{ opacity: isRemoved ? 0.5 : 1 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={isRemoved ? { textDecoration: 'line-through' } : undefined}
                                  >
                                    {refLabel}: <strong>{refValue || '—'}</strong>
                                  </Typography>
                                  {isRemoved && (
                                    <Chip label="To Be Removed" size="small" color="error" variant="outlined" />
                                  )}
                                </Stack>
                                <Tooltip title={isRemoved ? 'Restore item' : 'Remove item'}>
                                  <IconButton
                                    size="small"
                                    color={isRemoved ? 'primary' : 'error'}
                                    onClick={() => toggleDeleteItem(editItem.current.id)}
                                    disabled={isAnyLoading || !isEditable}
                                  >
                                    {isRemoved ? <RestoreIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                              </Stack>

                              {!isRemoved && (
                                <AtolItemContentEditor
                                  editItem={editItem}
                                  dateErrors={dateErrors}
                                  isLoading={isAnyLoading}
                                  onUpdateField={updateItemField}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Preview / Send Actions */}
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Button
                      variant="outlined"
                      color="primary"
                      size="large"
                      disabled={isAnyLoading || !hasAnyChanges || hasDateErrors || !isEditable}
                      onClick={handlePreview}
                      endIcon={patchMutation.isPending ? <Spinner size={24} color="grey" /> : null}
                    >
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={isAnyLoading || !hasAnyChanges || hasDateErrors || !isEditable}
                      onClick={handleSend}
                      endIcon={patchMutation.isPending ? <Spinner size={24} color="grey" /> : null}
                    >
                      Submit
                    </Button>
                    {hasAnyChanges && (
                      <Typography variant="body2" color="text.secondary">
                        {changeCount} item{changeCount === 1 ? '' : 's'} changed
                      </Typography>
                    )}
                  </Stack>

                  {hasDateErrors && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      Please fix the date errors above before previewing or submitting.
                    </Alert>
                  )}

                  {sendStatus && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity={sendStatus.success ? 'success' : 'error'} onClose={() => setSendStatus(null)}>
                        {sendStatus.message}
                      </Alert>
                    </Box>
                  )}

                  {previewMessage && !previewHtml && !patchMutation.isPending && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info">{previewMessage}</Alert>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Preview HTML viewer */}
              {previewHtml && !patchMutation.isPending && (
                <Card sx={{ mt: 3 }}>
                  <CardHeader
                    title="ATOL Certificate Preview"
                    subheader={
                      previewedAt ? `Previewed on: ${dayjs(previewedAt).format('DD MMM YYYY HH:mm:ss')}` : undefined
                    }
                  />
                  <CardContent>
                    {previewMessage && (
                      <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {previewMessage}
                        </Typography>
                      </Box>
                    )}
                    <AtolCertificateHtmlViewer certificateHtml={previewHtml} title="ATOL Certificate Preview" />
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
}
